import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { RequireRole } from '@/shared/auth/RequireRole'
import { useFeatures } from '@/shared/features/useFeatures'
import { EmptyState, LoadingPanel } from '@/shared/ui'

import type { PortalDefinition } from './portals'

export function RouteFallback() {
  const { t } = useTranslation('common')
  return <LoadingPanel label={t('state.loading')} />
}

export function NotFound() {
  const { t } = useTranslation('common')
  return <EmptyState title={t('error.notFound.title')} body={t('error.notFound.body')} />
}

/**
 * Course routes while education is switched off — roadmap 9.5.
 *
 * Sends `/courses` and `/courses/:id` to the announcement page. `replace` so
 * the back button returns where the visitor came from rather than bouncing them
 * through the redirect again.
 *
 * **This is a courtesy, not a boundary.** With the flag off the API does not
 * mount those routes at all, so the data behind this page is already gone —
 * this exists so a stale link lands somewhere that explains itself instead of
 * on a screen erroring against a 404.
 *
 * The loading state is not cosmetic. `features.education` is false until
 * `GET /config/` answers, so redirecting on the first render would bounce
 * *every* visitor to the announcement and then, a beat later, the ones whose
 * deployment has education on would be sent back — a visible flash through a
 * page that says the product does not exist. Waiting for `ready` costs one
 * spinner and gets the answer right the first time.
 */
export function RequireEducation({ children }: { children: React.ReactNode }) {
  const { features, ready } = useFeatures()

  if (!ready) return <RouteFallback />

  if (!features.education) return <Navigate to="/education" replace />

  return <>{children}</>
}

/**
 * Checkout, behind `features.payments`.
 *
 * ⚠️ `ready` is checked before the flag for the same reason `RequireEducation`
 * does it: `features.payments` is false until `GET /config/` answers, so
 * redirecting on first render would bounce every buyer and then send some of
 * them back — a visible flash through a page saying checkout does not exist.
 *
 * Redirects home rather than to an announcement page: there is no "payments
 * coming soon" screen and this plan does not add one.
 */
export function RequirePayments({ children }: { children: React.ReactNode }) {
  const { features, ready } = useFeatures()

  if (!ready) return <RouteFallback />

  if (!features.payments) return <Navigate to="/" replace />

  return <>{children}</>
}

/**
 * A portal behind its role guard, inside its own Suspense boundary.
 *
 * The boundary is per portal on purpose: it is what lets each chunk arrive
 * independently, so a slow instructor bundle never blanks the shell.
 */
export function PortalRoute({ portal }: { portal: PortalDefinition }) {
  const Portal = portal.component

  const content = (
    <Suspense fallback={<RouteFallback />}>
      <Portal />
    </Suspense>
  )

  // The learner surface is public. Wrapping it in a guard would send every
  // first-time visitor to a login form before they had seen anything.
  if (portal.allowAnonymous) return content

  return <RequireRole roles={portal.roles}>{content}</RequireRole>
}
